import { singularise } from "./shoppingList";

// Grocery-aisle categories. `SHOPPING_CATEGORIES` order is the order groups appear
// in on the list — roughly a walk through a store, with "Other" (the fallback for
// anything we can't classify) last.
export type ShoppingCategoryKey =
  | "produce"
  | "meat"
  | "dairy"
  | "bakery"
  | "pantry"
  | "frozen"
  | "household"
  | "other";

export interface ShoppingCategory {
  key: ShoppingCategoryKey;
  label: string;
}

export const SHOPPING_CATEGORIES: ShoppingCategory[] = [
  { key: "produce", label: "Produce" },
  { key: "meat", label: "Meat & Seafood" },
  { key: "dairy", label: "Dairy & Eggs" },
  { key: "bakery", label: "Bakery" },
  { key: "pantry", label: "Pantry" },
  { key: "frozen", label: "Frozen" },
  { key: "household", label: "Household" },
  { key: "other", label: "Other" },
];

// Multi-word phrases whose obvious keyword would send them to the wrong aisle
// ("coconut milk" isn't dairy, "fish sauce" isn't seafood). Checked first, as
// whole-string substrings, before the single-word token matching below.
const PHRASE_OVERRIDES: { key: ShoppingCategoryKey; phrase: string }[] = [
  { key: "pantry", phrase: "coconut milk" },
  { key: "pantry", phrase: "coconut cream" },
  { key: "pantry", phrase: "almond milk" },
  { key: "pantry", phrase: "oat milk" },
  { key: "pantry", phrase: "soy milk" },
  { key: "pantry", phrase: "soy sauce" },
  { key: "pantry", phrase: "fish sauce" },
  { key: "pantry", phrase: "oyster sauce" },
  { key: "pantry", phrase: "chicken stock" },
  { key: "pantry", phrase: "beef stock" },
  { key: "pantry", phrase: "vegetable stock" },
  { key: "pantry", phrase: "chicken broth" },
  { key: "pantry", phrase: "beef broth" },
  { key: "pantry", phrase: "vegetable broth" },
  { key: "frozen", phrase: "ice cream" },
];

// Single-word (already-singularised) tokens per aisle. Checked in this order, so a
// more specific aisle wins over a broad one (e.g. produce "bean" before pantry).
// Keywords are stored singular because the item's words are singularised first.
const KEYWORD_GROUPS: { key: ShoppingCategoryKey; words: string[] }[] = [
  {
    key: "household",
    words: [
      "foil", "wrap", "clingfilm", "cling", "baggie", "bag", "napkin", "towel",
      "detergent", "soap", "sponge", "cleaner", "bleach", "battery", "tissue",
      "toothpaste", "shampoo", "deodorant", "dishwasher", "trash", "bin",
    ],
  },
  {
    key: "frozen",
    words: ["frozen", "popsicle", "gelato"],
  },
  {
    key: "produce",
    words: [
      "apple", "banana", "orange", "lemon", "lime", "grape", "berry", "strawberry",
      "blueberry", "raspberry", "melon", "watermelon", "peach", "pear", "plum",
      "mango", "pineapple", "avocado", "kiwi", "cherry", "apricot", "fig",
      "lettuce", "spinach", "kale", "rocket", "arugula", "cabbage", "carrot",
      "celery", "cucumber", "tomato", "potato", "onion", "shallot", "garlic",
      "ginger", "pepper", "capsicum", "chilli", "chili", "jalapeno", "broccoli",
      "cauliflower", "zucchini", "courgette", "eggplant", "aubergine", "mushroom",
      "pea", "bean", "corn", "asparagus", "leek", "radish", "beet", "beetroot",
      "squash", "pumpkin", "parsnip", "turnip", "sprout", "herb", "basil",
      "parsley", "coriander", "cilantro", "mint", "thyme", "rosemary", "sage",
      "dill", "chive", "scallion", "spring onion",
    ],
  },
  {
    key: "meat",
    words: [
      "chicken", "beef", "pork", "lamb", "veal", "turkey", "duck", "bacon",
      "sausage", "mince", "steak", "ham", "prosciutto", "salami", "chorizo",
      "meat", "rib", "brisket", "thigh", "breast", "drumstick", "fillet",
      "fish", "salmon", "tuna", "cod", "prawn", "shrimp", "crab", "lobster",
      "mussel", "clam", "oyster", "scallop", "squid", "calamari", "anchovy",
      "sardine", "trout", "haddock", "tilapia",
    ],
  },
  {
    key: "dairy",
    words: [
      "milk", "cheese", "butter", "yogurt", "yoghurt", "cream", "egg",
      "margarine", "mozzarella", "cheddar", "parmesan", "feta", "ricotta",
      "brie", "mascarpone", "custard", "buttermilk", "ghee",
    ],
  },
  {
    key: "bakery",
    words: [
      "bread", "bun", "roll", "bagel", "baguette", "croissant", "tortilla",
      "pita", "naan", "muffin", "brioche", "sourdough", "ciabatta", "wrap",
      "crumpet", "donut", "doughnut", "pastry", "cake",
    ],
  },
  {
    key: "pantry",
    words: [
      "oil", "vinegar", "flour", "sugar", "salt", "rice", "pasta", "noodle",
      "spaghetti", "lentil", "chickpea", "quinoa", "oat", "cereal", "cracker",
      "biscuit", "sauce", "ketchup", "mustard", "mayonnaise", "mayo", "honey",
      "syrup", "jam", "peanut", "nut", "almond", "cashew", "walnut", "seed",
      "spice", "cumin", "paprika", "cinnamon", "nutmeg", "turmeric", "curry",
      "stock", "broth", "bouillon", "can", "tin", "coconut", "chocolate",
      "cocoa", "vanilla", "yeast", "soda", "powder", "raisin", "date", "tea",
      "coffee", "wine", "beer", "water", "juice", "stockcube", "passata",
      "tahini", "miso", "gelatin", "cornstarch", "cornflour",
    ],
  },
];

// Best-effort aisle for a shopping-list item, from its name alone. Offline and
// deterministic (no per-call cost); errs toward "other" rather than guessing wrong.
export function categoriseShoppingItem(name: string): ShoppingCategoryKey {
  const lower = name.toLowerCase();
  for (const { key, phrase } of PHRASE_OVERRIDES) {
    if (lower.includes(phrase)) return key;
  }
  const tokens = new Set(
    lower
      .split(/[^a-z]+/)
      .filter(Boolean)
      .map(singularise),
  );
  for (const group of KEYWORD_GROUPS) {
    for (const w of group.words) {
      if (w.includes(" ") ? lower.includes(w) : tokens.has(w)) return group.key;
    }
  }
  return "other";
}
